<template>
  <div ref="datePickerContainer" :class="['group has-left-icon', cls, {'has-right-icon': rightIcon}]">

    <div v-if="$slots.leftIcon" class="left-icon">
      <slot name="leftIcon"></slot>
    </div>
    <div class="left-icon" v-else>
      <i :class="['fi', iconMode[mode] ]"></i>
    </div>

    <date-picker v-model="model"
                 v-on="$listeners"
                 :popover="{ visibility: popoverVisibilityMode }"
                 @input="emitData('input', $event)"
                 :mode="mode"
                 :select-attribute="selectAttribute"
                 :is24hr="is24hr"
                 :is-range="isRange"
                 :locale="lang"
                 :masks="pickerMasks"
                 :model-config="modelConfig"
                 :valid-hours="validHours"
                 :max-date="max"
                 :min-date="min"
                 :input-debounce="700"
                 :first-day-of-week="fistDayOfWeek"
                 :minute-increment="minuteIncrement">
      <template v-slot="{ inputValue, inputEvents }">
        <div class="form-floating">
          <input :class="['form-control', {'error': localError}]"
                 :id="computedId"
                 :name="name"
                 :value="inputValue"
                 v-on="inputEvents"
                 :placeholder="placeholder ?? label"
                 autocomplete="off"
                 :required="required"
                 :disabled="disabled"/>
          <label :for="computedId">
            {{ label }} <span v-if="required">*</span>
          </label>
        </div>
      </template>
    </date-picker>

    <div v-if="$slots.rightIcon" class="right-icon">
      <slot name="rightIcon"></slot>
    </div>
    <div class="right-icon" v-else-if="help" data-bs-toggle="tooltip" data-bs-placement="top" :title="help">
      <i class="fi fi-rr-interrogation"></i>
    </div>
    <div class="right-icon" v-else-if="rightIcon">
      <i :class="['fi', rightIcon]"></i>
    </div>

    <div class="invalid-feedback d-block" v-if="localError">
      {{ localError }}
    </div>
  </div>
</template>

<script>
import {slugify} from "../../services/Defaults";
import DatePicker from "v-calendar/lib/components/date-picker.umd";
import {vCalendarDefault} from "../../services/Defaults";
import AbstractAtom from "./AbstractAtom.vue";

export default {
  name: "BasicDatePicker",
  components: {
    DatePicker
  },
  extends: AbstractAtom,
  props: {
    id: {
      type: String,
      required: false,
    },
    name: {
      type: String,
      required: false,
    },
    rightIcon: {
      type: String,
      required: false,
    },
    label: {
      type: String,
      required: false,
    },
    placeholder: {
      type: String,
      required: false,
    },
    value: {
      type: String | Number | Boolean,
      required: false,
    },
    mode: {
      type: String,
      required: false,
      default: 'dateTime'
    },
    required: {
      type: Boolean,
      required: false,
      default: false
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    help: {
      type: String,
      required: false,
    },
    error: {
      type: String,
      required: false,
    },
    min: {
      required: false,
    },
    max: {
      required: false,
    },
    selectAttribute: {
      type: Object,
      required: false,
      default: () => {
        return {
          highlight: {
            class: 'bg-light-primary',
            contentClass: 'text-primary',
          }
        }
      }
    },
    lang: {
      type: String,
      required: false,
      default: document.documentElement.lang
    },
    masks: {
      type: Object,
      required: false
    },
    validHours: {
      type: Object|Array|Function,
      required: false,
      default: () => {
        return {
          min: 6,
          max: 19
        }
      }
    },
    minuteIncrement: {
      type: Number,
      required: false,
      default: 15
    },
    is24hr: {
      type: Boolean,
      required: false,
      default: true
    },
    cls: String,
    popoverVisibilityMode: {
      type: String,
      required: false,
      default: 'hover'
    },
    isRange: {
      type: Boolean,
      required: false,
      default: false
    },
    fistDayOfWeek: {
      type: Number,
      required: false,
      default: 2
    }
  },
  computed: {
    computedId() {
      return this.id ?? slugify(this.label ?? this.name ?? this.placeholder ?? 'custom');
    },
    modes() {
      return {
        date: vCalendarDefault.dataInputDate,
        dateTime: vCalendarDefault.dataInput,
        time: vCalendarDefault.dataInputTime
      };
    },
    modelConfig() {
      return {
        type: 'string',
        mask: this.modes[this.mode],
      };
    },
    pickerMasks() {
      if (this.mode === 'date') {
        return {
          L: this.modes.date,
          input: this.modes.date,
          data: this.modes.date
        };
      } else if (this.mode === 'time') {
        return  {
          L: this.modes.date,
          input: this.modes.dateTime,
          data: this.modes.date
        }
      }
      else {
        return {
          L: this.modes.date,
          input: this.modes.dateTime,
          data: this.modes.date
        };
      }
    }
  },
  watch: {
    value: function (val) {
      this.model = val
    },
    model: function (val) {
      this.emitData('input', val);
    },
    error: function (val) {
      this.localError = val
    }
  },
  data() {
    return {
      model: this.value || '',
      iconMode: {
        date: 'fi-rr-calendar',
        dateTime: 'fi-rr-calendar-clock',
        time: 'fi-rr-clock'
      },
      localError: this.error
    }
  },
  methods: {
    emitData(event, action) {
      this.localError = null;
      this.$emit('reset-error');
      this.$emit(event, action);
    },
    onFocusOut(event) {
      if (!this.$refs.datePickerContainer.contains(event.relatedTarget)) {
        this.$children[0]?.$refs?.popover?.hide()
      }
    },
  },
  mounted() {
    this.$refs.datePickerContainer.addEventListener('focusout', this.onFocusOut);
  },
  beforeDestroy() {
    this.$refs.datePickerContainer.removeEventListener('focusout', this.onFocusOut);
  }
}
</script>

